import { mixpanel } from '@/utils/helperFunctions'
import { AxiosPromise, AxiosRequestConfig } from 'axios'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
import axios from '@/utils/ajax/http'

const days = [
  { text: 'Sunday', value: 0, sessionsPerWeek: 1 },
  { text: 'Monday', value: 1, sessionsPerWeek: 1 },
  { text: 'Tuesday', value: 2, sessionsPerWeek: 1 },
  { text: 'Wednesday', value: 3, sessionsPerWeek: 1 },
  { text: 'Thursday', value: 4, sessionsPerWeek: 1 },
  { text: 'Friday', value: 5, sessionsPerWeek: 1 },
  { text: 'Saturday', value: 6, sessionsPerWeek: 1 },
  { text: 'Monday - Thursday', value: 7, sessionsPerWeek: 4 },
  { text: 'Monday & Thursday', value: 8, sessionsPerWeek: 2 },
  { text: 'Tuesday & Friday', value: 9, sessionsPerWeek: 2 },
  { text: 'Monday - Friday', value: 10, sessionsPerWeek: 5 },
  { text: 'Tuesday - Saturday', value: 11, sessionsPerWeek: 5 },
  { text: 'Monday & Tuesday', value: 12, sessionsPerWeek: 2 },
  { text: 'Saturday & Sunday', value: 13, sessionsPerWeek: 2 },
]

export default {
  async getSummerClasses(track: string): Promise<APISummerClassesResponse> {
    // TODO: optional term query
    const response = await axios.get(`/enroll/api/class/summer?track=${track}`)
    return { ...response.data }
  },

  async getSummerCamps(grade: string): Promise<APISummerClassesResponse> {
    // TODO: optional term query
    const response = await axios.get(`/enroll/api/summer/camps?grade=${grade}`)
    return response.data
  },

  async markWelcomeVideoWatched(studentId: number) {
    const response = await axios.post(`/enroll/api/welcome_video`, {
      studentId,
    })
    return response.data
  },

  async getSources(): Promise<APIAcquisitionSourcesResponse> {
    const response = await axios.get('/enroll/api/sources')
    return { ...response.data }
  },

  async getSMSTypes(phoneNumber: number | string): Promise<APICommsResponse> {
    const toSend = { params: { phoneNumber } } as AxiosRequestConfig
    const response = await axios.get('/enroll/api/comms', toSend)
    return { ...response.data }
  },

  /**
   * Load a list of formatted timezones from the backend.
   */
  async getTimezones(): Promise<APITZListResponse> {
    const response = await axios.get('/enroll/api/timezones')
    return { ...response.data }
  },

  /**
   * Final step - register the user with the backend
   * @param data
   */
  async submitCheckout(data: APICheckoutOptions): Promise<APICheckoutResponse> {
    const response = await axios.post('/enroll/api/checkout', data)
    return { ...response.data }
  },

  async submitPlacementCheckout(
    data: APICheckoutOptions
  ): Promise<APICheckoutResponse> {
    const response = await axios.post('/enroll/api/placement-checkout', data)
    return { ...response.data }
  },

  async submitSummerCheckout(data: APICheckoutOptions) {
    const response = await axios.post('/enroll/api/summer/checkout', data)
    return { ...response.data }
  },

  async getEnrollmentTotal(data: APIGetTotalOptions): Promise<APIGetTotalResponse> {
    const toSend = { params: data } as AxiosRequestConfig
    const response = await axios.get('/enroll/api/total', toSend)
    return { ...response.data }
  },

  async getSummerEnrollmentTotal(
    data: APIGetTotalOptions
  ): Promise<APIGetTotalResponse> {
    const response = await axios.post('/enroll/api/total/summer', data)
    return { ...response.data }
  },

  validateEmail(address: string): Promise<AxiosPromise> {
    return axios.get('/enroll/api/validate', {
      params: { email: address },
    } as AxiosRequestConfig)
  },

  checkDuplicate(firstName: string, lastName: string, email: string) {
    const config = { params: { firstName, lastName, email } }
    return axios.get<DupeCheckResponse>('/enroll/api/username', config)
  },

  /**Formats the slot time for an SE class and the timezone passed in. */
  fmtSlotTimeFunnel(
    cls: SEClass,
    tz: TZModel | { timezone: string } = { timezone: 'America/Chicago' }
  ): { dateText: string; timeText: string; rangeText: string; start: dayjs.Dayjs } {
    const start = dayjs(cls.classStart + 'Z').tz(tz.timezone)
    const lastClassSession = dayjs(cls.lastClassSession + 'Z').tz(tz.timezone)

    let symbol: string
    let dateText = ''
    if (cls.daySpan.type === 'SINGLE') dateText = start.format('dddd')
    else if (cls.daySpan.type === 'DOUBLE') {
      symbol = '&'
    } else {
      symbol = '-'
    }

    if (cls.daySpan.type !== 'SINGLE') {
      dateText = `${start.format('dddd')} ${symbol} ${start
        .add(cls.daySpan.otherDay, 'days')
        .format('dddd')}`
    }

    const timeText = `${start.format('h:mm A')} - ${start
      .add(cls.classLengthInMinutes, 'minutes')
      .format('h:mm A z')}`

    const rangeText = `${start.format('MM/DD/YYYY')} - ${dayjs(
      lastClassSession
    ).format('MM/DD/YYYY')}`

    return { dateText, timeText, rangeText, start }
  },

  fmtSlotTime(model: ClassModel, tz?: TZModel | { timezone: string }): string {
    let frequency: string
    let start = dayjs.unix(model.startDate)
    const [y, m, d] = model.classEnd.split('-').map((s) => Number(s))
    const end = dayjs(new Date(y, m - 1, d))
    const day = Number(model.day)

    // handle tz conversion
    if (tz) start = dayjs.tz(start, tz.timezone)

    const startTime = start.format('h:mm A')

    let classLengthInHours = 1
    if (model.isCamp) classLengthInHours = 2

    const endTime = start
      .add(classLengthInHours, 'h')
      .format(`h:mm A ${tz ? `[${tz.timezone}]` : 'z'}`)
    const dayRange = ` ${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`
    const timeRange = `${startTime} - ${endTime}`

    if (model.day !== undefined) {
      frequency = days.find((d) => d.value === day).text
    }

    if (day < 7) frequency += 's'

    // prepend a message if class is full
    let fullCheck = ''
    if (model.openSeats < 1) fullCheck = 'CLASS FULL! '

    return `${fullCheck}${frequency}, ${timeRange}, ${dayRange}`
  },
  getSessionsPerWeek(val: number) {
    return days.find((d) => d.value === val)?.sessionsPerWeek
  },

  fmtCampSlotTime(model: ClassModel, tz?: TZModel): string {
    let frequency: string
    let start = dayjs.unix(model.startDate)

    // handle tz conversion
    if (tz) {
      start = dayjs.tz(start, tz.timezone)
    }

    const startTime = start.format('h:mm A')
    const endTime = start
      .add(2, 'h')
      .format(`h:mm A ${tz ? `[${tz.timezone}]` : 'z'}`)

    const timeRange = `${startTime} - ${endTime}`

    const startDateFmt = start.format('MMMM Do')

    if (model.day === 10) {
      frequency = 'Monday - Friday'
    } else if (model.day === 11) {
      frequency = 'Tuesday - Saturday'
    } else if (model.day === 12) {
      frequency = 'Monday & Tuesday'
    } else if (model.day === 13) {
      frequency = 'Saturday & Sunday'
    } else {
      frequency = 'Error'
    }

    // prepend a message if class is full
    let fullCheck = ''
    if (model.openSeats < 1) fullCheck = 'CLASS FULL! '

    return `${fullCheck}${frequency}, ${timeRange}, Starting ${startDateFmt}`
  },

  fmtSpecialSlotTime(model: ClassModel, tz?: string): string {
    let start = dayjs.unix(model.startDate)

    if (tz) start = start.tz(tz)
    const startTime = start.format('h:mm A z')
    const startDateFmt = start.format('MMMM Do')
    const day = start.format('dddd')

    // prepend a message if class is full
    let fullCheck = ''
    if (model.openSeats < 1) fullCheck = 'CLASS FULL! '

    return `${fullCheck}${day} at ${startTime}, On ${startDateFmt}`
  },

  // TODO: this is a bandaid for black friday 2021, should be handled on back end later
  fmtBlackFridayClassNames(courseStr: string) {
    if (courseStr === 'h112_intro_python') return 'What Superhero Are You Program'
    if (courseStr === 'm112_intro_prog_py') return 'Web Avatar Python Project'
    if (courseStr === 'e112_scratch1') return 'Ping Pong Scratch Game'
    return courseStr
  },

  monthAbbreviation(model: ClassModel): string {
    return dayjs.unix(model.startDate).format('MMM')
  },

  /**
   * Push an event to window.dataLayer for Google Tag Manager to read.
   * @param event
   */
  pushGtm(event: Record<string, string>): void {
    ;(window as any).dataLayer.push(event)
  },

  mixpanel,
  gtagEvent(event: string, parameters?: Record<string, any>) {
    const NODE_ENV = import.meta.env.VITE_NODE_ENV

    if (!window.gtag) {
      console.log('gtm.js not loaded')
      return
    }

    if (NODE_ENV !== 'production') {
      console.log('firing gtag stage_', event, 'with params: ', parameters)
      gtag('event', 'stage_' + event, parameters)
    } else {
      console.log('firing gtag', event, 'with params: ', parameters)
      gtag('event', event, parameters)
    }
  },

  async checkoutSuccess(nonce: string): Promise<APICheckoutResponse> {
    const response = await axios.post<APICheckoutResponse>(
      '/enroll/api/checkout-success',
      {
        nonce,
      }
    )
    return response.data
  },

  async markGtmEventFired(nonce: string) {
    await axios.post('/enroll/api/gtm-event-fired', { nonce })
  },

  async getPrice(lookupKey: string): Promise<number> {
    const response = await axios.get<{ amount: number }>('/enroll/api/price', {
      params: { lookupKey },
    })
    return response.data.amount
  },

  centsToDollars(cents: number) {
    const negative = cents < 0
    let dollars = cents / 100
    if (negative) {
      dollars *= -1
      return `($${dollars.toFixed(2)})`
    }
    return `$${dollars.toFixed(2)}`
  },
}
